import { createSlice } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'

export type NotificationType = 'info' | 'warning' | 'error' | 'job'
export interface Notification {
    type: NotificationType
    msg: string,
    expireTime: any,
    key: string,
    id: any,
    name: string,
    pinned: boolean,
    data: any
}

const initialState = {
    notifications: [
        // { type: 'info', msg: 'test', expireTime: null, key: '1', id: 1, name: '', pinned: false, data: null },
    ] as Notification[],
    keys: [] as string[],
    timersMap: {} as any,
}

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        addNotifications: (state, action) => {
            state.notifications = [...state.notifications, ...action.payload]
        },
        updateNotification: (state, action) => {
            state.notifications = state.notifications.map((n: any) => {
                if(n.id === action.payload.id) {
                    return {...n, ...action.payload}
                }
                return n
            })
        },
        removeNotificationsByName: (state, action) => {
            state.notifications = state.notifications.filter(n => n.name !== action.payload)
        },
        removeNotificationsByKey: (state, action) => {
            state.notifications = state.notifications.filter(n => n.key !== action.payload)
        },
        removeNotificationsById: (state, action) => {
            state.notifications = state.notifications.filter(n => n.id !== action.payload)
        },
        addTimerId: (state, action) => {
            state.timersMap[action.payload.notificationId] = action.payload.timerId
        },
        removeTimerId: (state, action) => {
            delete state.timersMap[action.payload]
        },
        clearNotifications: (state) => {
            state.notifications = state.notifications.filter(n => n.type === 'job')
        },
        setPinned: (state, action) => {
            state.notifications = state.notifications.map(n => n.id === action.payload.id ? {...n, pinned: action.payload.pin} : n)
        },
        addKey: (state, action) => {
            // new keys are added to the start of the array
            state.keys.unshift(action.payload)
        },
        removeKey: (state, action) => {
            state.keys = state.keys.filter(k => k !== action.payload)
        },
    }
})

const { removeNotificationsByName, updateNotification, addNotifications,
    clearNotifications, removeNotificationsByKey, removeNotificationsById,
    addTimerId, removeTimerId, setPinned, addKey, removeKey } = notificationsSlice.actions

const updateNotificationThunk = (notification: any) => {
    return (dispatch: Function, getState: Function) => {
        const { timersMap } = getState().notifications

        clearTimeout(timersMap[notification.id])
        const timerId = setTimeout(() => {
            const { notifications } = getState().notifications
            const isPinnedNotification = notifications.find((i: any) => i.id === notification.id)?.pinned

            if (!isPinnedNotification) {dispatch(removeNotificationsById(notification.id))}
            dispatch(removeTimerId(notification.id))
        }, notification.expireTime)

        dispatch(addTimerId({ notificationId: notification.id, timerId }))
        dispatch(updateNotification(notification))
    }
}

const removeNotificationsByKeyThunk = (key: string) => {
    return (dispatch: Function, getState: Function) => {
        const { notifications, timersMap } = getState().notifications

        const notificationsByKey = notifications.filter((n: any) => n.key === key)

        notificationsByKey.forEach((n: any) => {
            if (timersMap[n.id]) {
                clearTimeout(timersMap[n.id])
                dispatch(removeTimerId(n.id))
            }
        })

        dispatch(removeNotificationsByKey(key))
        dispatch(removeKey(key))
    }
}

const removeNotificationsByIdThunk = (id: any) => {
    return (dispatch: Function, getState: Function) => {
        const { timersMap } = getState().notifications

        clearTimeout(timersMap[id])
        dispatch(removeNotificationsById(id))
    }
}

const setNotificationsByKeyThunk = (notifications: any[], key: string, removeOldNotification: boolean=true) => {
    return (dispatch: Function, getState: Function) => {

        // remove the old notifications
        removeOldNotification && dispatch(removeNotificationsByKeyThunk(key))

        // prep and add the new notifications
        const prepedNotifications = notifications.map(n => ({
            ...n,
            id: n.type === 'job' ? n.data.HDR_ID.toString() : uuidv4(),
            key: key
        }))
        prepedNotifications.forEach((n: any) => {
            if (n.expireTime) {
                const timerId = setTimeout(() => {
                    const { notifications } = getState().notifications
                    const isPinnedNotification = notifications.find((i: any) => i.id === n.id)?.pinned

                    if (!isPinnedNotification) {dispatch(removeNotificationsById(n.id))}
                    dispatch(removeTimerId(n.id))
                }, n.expireTime)
                dispatch(addTimerId({ notificationId: n.id, timerId }))
            }
        })
        dispatch(addNotifications(prepedNotifications))
        dispatch(addKey(key))
    }
}

const  toggleTimeoutIdThunk = (currentId: any) => {
    return (dispatch: Function, getState: Function) => {
        const { notifications, timersMap } = getState().notifications
        const notification = notifications.find((n: any) => n.id === currentId)

        if (notification.pinned) {
            if (!timersMap[currentId]) {dispatch(removeNotificationsById(currentId))}
            dispatch(setPinned({id: notification.id, pin: false}))
        } else {
            dispatch(setPinned({id: notification.id, pin: true}))
        }
    }
}

const clearNotificationsThunk = (() =>{
    return (dispatch: Function, getState: Function) => {
        const { timersMap, notifications } = getState().notifications
        Object.keys(timersMap).forEach((id: any) => {
            notifications.forEach((n: any) => {
                (n.type !== 'job' && n.id === id) && clearTimeout(timersMap[id])
            })

        })
        notifications.forEach((n: any) => ((n.type !== 'job' && n.expireTime) && dispatch(removeTimerId(n.id))) )
        dispatch(clearNotifications())
    }
} )

export {
    updateNotification,
    addNotifications,
    clearNotifications,
    removeNotificationsByKey,
    setPinned,
    removeNotificationsById,
    addTimerId,
    removeTimerId,
    clearNotificationsThunk,
    removeNotificationsByKeyThunk,
    setNotificationsByKeyThunk,
    toggleTimeoutIdThunk,
    removeNotificationsByName,
    updateNotificationThunk,
    removeNotificationsByIdThunk,
}
export default notificationsSlice.reducer
